import React from 'react';
import { InputBaseProps, StaticInputField } from './InputBase';
import SelectBase from './SelectBase';
import { View } from 'react-native';
import { AvatarStack, LoadableAvatar, getFallbackForGenericString } from '../Avatar';
import { SelectListPrompt } from '../Prompt';
import PressableDynamicOpacity from '../PressableDynamicOpacity';
import { getSpacing } from '../../../utils/spacing';
import Stack, { contentStackSpacing } from '../Stack';
import Text from '../Text';
import { getIntl } from '../../../utils/intl';
import _ from 'lodash';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';

const intl = getIntl('profile-multi-select', {
    allProfiles: 'All Profiles',
});

export type ProfileSelectOption = {
    avatarUrl: string;
    label: string;
    value: string;
    testID?: string;
};

export type ProfileSelectOwnProps = {
    value: string | null;
    onChange: (value: string | null) => void;
    selectOptions: ProfileSelectOption[];
};

export type ProfileSelectProps = ProfileSelectOwnProps &
    Omit<InputBaseProps, 'renderInputField' | 'onInputPress' | 'value' | 'onChange'>;

export type ProfileMultiSelectOwnProps = {
    value: string[];
    onChange: (value: string[]) => void;
    selectOptions: ProfileSelectOption[];
};

export const ProfileSelect: React.FC<ProfileSelectProps> = (props) => {
    const { value, onChange, selectOptions, ...toPass } = props;

    return (
        <SelectBase
            {...toPass}
            forceFullWidth
            onSelect={(value: string) => {
                onChange(value === props.value ? null : value);
            }}
            inputValue={props.value ? 'value' : null}
            renderInputValue={() => {
                const nullValue = (
                    <View style={{ flex: 1 }}>
                        <StaticInputField value={null} />
                    </View>
                );
                if (!value) return nullValue;

                const selectedOption = selectOptions.find((option) => option.value === value);
                if (!selectedOption) return nullValue;

                return (
                    <Stack horizontal justify="flex-start" align="center" space={2} flex={1}>
                        <LoadableAvatar
                            size={'xs'}
                            avatarUrl={selectedOption.avatarUrl}
                            fallbackName={getFallbackForGenericString(selectedOption.label)}
                        />
                        <Text variant={'input-text'}>{selectedOption.label}</Text>
                    </Stack>
                );
            }}
            renderMobileOptions={({ hasFocus, onSelect, onCloseSelect, onOpenSelect }) => (
                <SelectListPrompt
                    value={value}
                    options={[
                        ...selectOptions.map((option) => ({
                            label: option.label,
                            value: option.value,
                            testID: option.testID,
                            leftContent: (
                                <LoadableAvatar
                                    size={'sm'}
                                    avatarUrl={option.avatarUrl}
                                    fallbackName={getFallbackForGenericString(option.label)}
                                />
                            ),
                        })),
                    ]}
                    renderSelectButtonContent={({ left, center, right }) => (
                        <Stack horizontal fullWidth justify="space-between" align="center">
                            <Stack horizontal flex={1} align="center" space={contentStackSpacing}>
                                {left}
                                {center}
                            </Stack>
                            {right}
                        </Stack>
                    )}
                    title={props.title}
                    onChange={() => {}} // ignore, we manage the complex all-select logic in onSelect
                    onSelect={onSelect}
                    isOpen={hasFocus}
                    setIsOpen={(isOpen) => {
                        isOpen ? onOpenSelect() : onCloseSelect();
                    }}
                />
            )}
            renderDesktopOptions={({ onSelect }) => {
                return (
                    <>
                        {selectOptions.map((option) => (
                            <PressableDynamicOpacity
                                key={option.value}
                                onPress={() => {
                                    onSelect(option.value);
                                }}
                                style={{
                                    width: '100%',
                                    paddingTop: getSpacing(4),
                                    paddingBottom: getSpacing(2),
                                }}
                                testID={`select-option-${option.testID || option.value}`}
                            >
                                <Stack
                                    horizontal
                                    justify="flex-start"
                                    align="center"
                                    testID={option.label}
                                    space={contentStackSpacing}
                                >
                                    <LoadableAvatar
                                        size={'sm'}
                                        avatarUrl={option.avatarUrl}
                                        fallbackName={getFallbackForGenericString(option.label)}
                                    />
                                    <Text variant={value === option.value ? 'selected-select-option' : 'select-option'}>
                                        {option.label}
                                    </Text>
                                </Stack>
                            </PressableDynamicOpacity>
                        ))}
                    </>
                );
            }}
        />
    );
};

export type ProfileMultiSelectProps = ProfileMultiSelectOwnProps &
    Omit<InputBaseProps, 'renderInputField' | 'onInputPress' | 'value' | 'onChange'>;

const ProfileMultiSelect: React.FC<ProfileMultiSelectProps> = (props) => {
    const { value, onChange, selectOptions, ...toPass } = props;
    const isMobile = useIsMobileLayout();
    const allSelected = props.value.length === props.selectOptions.length;
    return (
        <SelectBase
            {...toPass}
            forceFullWidth
            valueContainerMinHeight={props.value.length > 0 ? 40 : undefined}
            onSelect={(value: string) => {
                const allRemoved = value === 'all' && allSelected;
                if (allRemoved) {
                    // If all are selected and we are deselecting all, we should clear the selection
                    onChange([]);
                } else if (value === 'all') {
                    // If we are selecting all, we should select all
                    onChange(props.selectOptions.map((option) => option.value));
                } else if (allSelected) {
                    // If all are selected and we are selecting a single option, we should only select that option
                    onChange([value]);
                } else {
                    if (props.value.includes(value)) {
                        onChange(props.value.filter((val) => val !== value));
                    } else {
                        onChange([...props.value, value]);
                    }
                }
            }}
            inputValue={props.value.length > 0 ? 'value' : null}
            renderInputValue={() => (
                <View
                    style={{ flex: 1, justifyContent: 'flex-start', alignItems: 'center', flexDirection: 'row' }}
                    collapsable={false}
                >
                    <AvatarStack
                        items={props.selectOptions
                            .filter((_) => props.value.includes(_.value))
                            .map((_) => ({
                                type: 'medicalProfile',
                                medicalProfileId: _.value,
                            }))}
                        displayLimit={isMobile ? 6 : 18}
                    />
                    {props.value.length ? (
                        <Text
                            variant="body-1"
                            style={{ marginLeft: 20 }}
                        >{`${props.value.length}/${props.selectOptions.length}`}</Text>
                    ) : null}
                </View>
            )}
            renderMobileOptions={({ hasFocus, onSelect, onCloseSelect, onOpenSelect }) => (
                <SelectListPrompt
                    value={allSelected ? ['all'] : props.value}
                    multiSelect
                    options={[
                        ...selectOptions.map((option) => ({
                            label: option.label,
                            value: option.value,
                            testID: option.testID,
                            leftContent: (
                                <LoadableAvatar
                                    size={'sm'}
                                    avatarUrl={option.avatarUrl}
                                    fallbackName={getFallbackForGenericString(option.label)}
                                />
                            ),
                        })),
                        {
                            label: intl('allProfiles'),
                            value: 'all',
                        },
                    ]}
                    renderSelectButtonContent={({ left, center, right }) => (
                        <Stack horizontal fullWidth justify="space-between" align="center">
                            <Stack horizontal flex={1} align="center" space={contentStackSpacing}>
                                {left}
                                {center}
                            </Stack>
                            {right}
                        </Stack>
                    )}
                    title={props.title}
                    onChange={() => {}} // ignore, we manage the complex all-select logic in onSelect
                    onSelect={onSelect}
                    isOpen={hasFocus}
                    setIsOpen={(isOpen) => {
                        isOpen ? onOpenSelect() : onCloseSelect();
                    }}
                />
            )}
            renderDesktopOptions={({ onSelect }) => {
                return (
                    <>
                        {selectOptions.map((option) => (
                            <PressableDynamicOpacity
                                key={option.value}
                                onPress={() => {
                                    onSelect(option.value);
                                }}
                                style={{
                                    width: '100%',
                                    paddingTop: getSpacing(4),
                                    paddingBottom: getSpacing(2),
                                }}
                                testID={`select-option-${option.testID || option.value}`}
                            >
                                <Stack
                                    horizontal
                                    justify="flex-start"
                                    align="center"
                                    testID={
                                        value.includes(option.value) && !allSelected
                                            ? `selected-select-option-${option.value}`
                                            : `not-selected-select-option-${option.value}`
                                    }
                                    space={contentStackSpacing}
                                >
                                    <LoadableAvatar
                                        size={'sm'}
                                        avatarUrl={option.avatarUrl}
                                        fallbackName={getFallbackForGenericString(option.label)}
                                    />
                                    <Text
                                        variant={
                                            value.includes(option.value) && !allSelected
                                                ? 'selected-select-option'
                                                : 'select-option'
                                        }
                                    >
                                        {option.label}
                                    </Text>
                                </Stack>
                            </PressableDynamicOpacity>
                        ))}
                        <PressableDynamicOpacity
                            key={'all'}
                            onPress={() => {
                                onSelect('all');
                            }}
                            style={{
                                width: '100%',
                                paddingTop: getSpacing(4),
                                paddingBottom: getSpacing(2),
                            }}
                            testID={`select-option-all`}
                        >
                            <Stack
                                horizontal
                                justify="flex-start"
                                align="center"
                                testID={allSelected ? `selected-select-option-all` : `not-selected-select-option-all`}
                                space={contentStackSpacing}
                            >
                                <Text variant={allSelected ? 'selected-select-option' : 'select-option'}>
                                    {intl('allProfiles')}
                                </Text>
                            </Stack>
                        </PressableDynamicOpacity>
                    </>
                );
            }}
        />
    );
};

export default React.memo(ProfileMultiSelect);
